<template>
  <div
      ref="root"
      :class="isCurrent ? 'tw-bg-infinity-row-active-background' : 'hover:tw-bg-infinity-row-hover-background'"
      class="row-beat-container tw-flex sm:tw-grid sm:tw-gap-4 sm:tw-space-x-0 tw-grid-flow-col tw-items-center tw-justify-start tw-grid-cols-12 tw-group tw-p-2 tw-border-infinity-background-emphasized tw-border-0 tw-border-b tw-border-solid tw-cursor-pointer tw-rounded"
      @click="onClick"
  >
    <!-- Chart Number | Artwork | Title | Genre   -->
    <div class="tw-col-span-6 md:tw-col-span-4 tw-truncate tw-mr-4 sm:tw-mr-0">
      <div class="tw-flex tw-space-x-3 tw-items-center tw-justify-items-start">
        <!-- Chart Number -->
        <div class="tw-w-3 tw-flex-shrink-0 tw-text-xs tw-text-infinity-tertiary-text" v-if="chartNumber">{{ chartNumber
          }}
        </div>

        <!-- Artwork -->
        <div class="tw-w-13 tw-h-13 tw-flex-shrink-0">
          <Artwork class="tw-rounded tw-overflow-hidden" :src="beat.image(imageSize)" :active="isCurrent"
                   :image-size="imageSize">
            <PlayButton slot="center"
                        :size="'small'"
                        :playing="isPlaying" />
          </Artwork>
        </div>

        <!-- Title | Genre  -->
        <div class="tw-truncate">
          <router-link
              :class="isCurrent ? 'tw-text-infinity-row-active-primary-text hover:tw-text-infinity-row-active-primary-text' : 'group-hover:tw-text-infinity-row-hover-primary-text'"
              class="tw-truncate tw-transition tw-font-bold hover:tw-underline"
              @click.native.stop
              :to="{name: 'beat', params: {beat: beat.alias}}">{{ beat.name }}
          </router-link>
          <div
              :class="isCurrent ? 'tw-text-infinity-row-active-secondary-text' : 'group-hover:tw-text-infinity-row-hover-secondary-text'"
              class="tw-transition tw-text-infinity-tertiary-text tw-text-xxs tw-font-light">
            {{ beat.genre.name }}
          </div>
        </div>
      </div>
    </div>

    <!-- Duration -->
    <div class="tw-hidden sm:tw-block tw-col-span-1 tw-text-center">
      <span
          :class="isCurrent ? 'tw-text-infinity-row-active-secondary-text' : 'group-hover:tw-text-infinity-row-hover-secondary-text'"
          class="tw-transition tw-text-infinity-tertiary-text tw-text-xxs tw-font-light">{{ beat.duration }}</span>
    </div>

    <!-- Tempo -->
    <div class="tw-hidden sm:tw-block tw-col-span-1 tw-text-center">
      <span
          :class="isCurrent ? 'tw-text-infinity-row-active-secondary-text' : 'group-hover:tw-text-infinity-row-hover-secondary-text'"
          class="tw-transition tw-text-infinity-tertiary-text tw-text-xxs tw-font-light">{{ beat.tempo }}</span>
    </div>

    <!-- Tags | Button  -->
    <div class="tw-col-span-4 md:tw-col-span-6 tw-grid tw-grid-flow-col tw-gap-4 tw-items-center tw-ml-auto sm:tw-ml-0">
      <!-- Tags -->
      <div class="md:tw-block tw-hidden tw-truncate">
        <template v-if="beat.tags">
          <div
              :class="isCurrent ? 'tw-text-infinity-row-active-secondary-text' : 'group-hover:tw-text-infinity-row-hover-secondary-text'"
              class="tw-transition tw-text-infinity-tertiary-text tw-text-xxs tw-font-light">{{ tags }}
          </div>
        </template>
      </div>

      <!-- Buttons -->
      <div class="tw-ml-auto tw-flex-shrink-0 tw-justify-self-end">
        <div class="tw-grid tw-items-center tw-inline-grid tw-grid-flow-col tw-gap-5">
          <div class="tw-hidden sm:tw-grid tw-grid-flow-col tw-gap-4">
            <download-button class="tw-text-infinity-success" :item="beat"
                             v-if="showDownload && beat.freeDownload"></download-button>
            <share-button class="tw-text-infinity-secondary-text" :parent="this.parent" :item="beat"></share-button>
          </div>
          <buy-button :item="beat"></buy-button>

          <!-- Mobile Menu -->
          <div class="sm:tw-hidden" ref="dropdown" @click.stop>
            <base-popover @click.stop container="#content" placement="bottom-start" :key="beat.id">
              <button class="tw-w-10 tw-h-12 tw--ml-2.5 focus:tw-text-infinity-button-background tw-text-infinity-secondary-text tw-border-none tw-bg-transparent"
                      type="button">
                <font-awesome-icon icon="ellipsis-h" class="tw-text-base" />
              </button>
              <div slot="popover">
                <buy-button :item="beat"
                            class="c-popover-list-item"
                            v-if="!beat.inCart()"
                            v-close-popover
                            variant="basic">
                </buy-button>
                <download-button :item="beat"
                                 v-close-popover
                                 class="c-popover-list-item"
                                 v-if="showDownload && beat.freeDownload"
                                 variant="basic">
                </download-button>
                <share-button :item="beat"
                              v-close-popover
                              class="c-popover-list-item"
                              variant="basic"
                              :parent="this.parent">
                </share-button>
                <router-link :to="{name: 'beat', params: {beat: beat.alias}}"
                             v-close-popover
                             class="c-button c-popover-list-item">
                  <font-awesome-icon class="c-button__left-icon" icon="info-circle"></font-awesome-icon>
                  <span>View Details</span>
                </router-link>
              </div>
            </base-popover>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Artwork from "./base/base-artwork";
import PlayButton from "./base/base-play";
import DownloadButton from "./shared/download-button.vue";
import ShareButton from "./shared/share-button.vue";
import BasePopover from "./base/base-popover.vue";
import BuyButton from "./shared/buy-button.vue";

export default {
  props: {
    parent: {
      type: Object,
      default: null
    },
    beat: {
      type: Object,
      required: true
    },
    showPrice: {
      type: Boolean,
      default: false
    },
    showDownload: {
      type: Boolean,
      default: false
    },
    showMeta: {
      type: Boolean,
      default: false
    },
    imageSize: {
      type: [Number, String],
      default: 100
    },
    chartNumber: {
      type: [Number, String, Boolean],
      default: false
    }
  },
  created() {
    // Legacy logic, no idea why :/
    this.$data.$display = "list";
  },
  components: {
    DownloadButton, ShareButton, BasePopover, BuyButton,
    Artwork,
    PlayButton
  },
  data() {
    return {
      isCurrent: false,
    }
  },
  mounted() {
    this.repaint()
  },
  computed: {
    tags() {
      const { tags } = this.beat;
      if (tags?.length) {
        return `#${tags.map((tag) => tag.name).join("   #")}`;
      }
    },
    isActive() {
      return this.playlistManager.isPlaying(this.beat);
    },
    playerActive() {
      return this.$store.state.player.playing;
    },
    playlistManager() {
      return this.$store.state.playlistManager;
    },
    isPlaying() {
      return this.playlistManager.isPlaying(this.beat) && this.playerActive;
    },
    isPaused() {
      return this.playlistManager.isPlaying(this.beat) && !this.playerActive;
    }
  },
  methods: {
    repaint() {
      /**
       * There is a bug in iOS 15.5 that cause improper rendering of grid layout,
       * this hack is a workaround for that.
       * */
      this.$nextTick(() => {
        this.$refs.root.style.display = 'none'
        setTimeout(() => {
          this.$nextTick(() => {
            this.$refs.root.style.display = ''
          })
        })
      })
    },
    onClick($event) {
      if (this.$refs.dropdown.contains($event.target)) return;
      this.isCurrent = true;
      if (this.isPlaying) {
        this.beat.pause();
      } else {
        this.beat.play();
      }
    }
  },
  watch: {
    isActive(active) {
      this.isCurrent = active
    }
  }
};
</script>
<style lang="scss" scoped>
.row-beat-container {
  &:last-of-type {
    border-bottom: none;
  }
}
</style>
